import { useEffect, useState } from "react";
import {
    Button, Card, Divider, Empty, Form, Input, InputNumber, Modal, Select, Space, Table, Tag, Tooltip, Typography,
} from "antd";
import {
    BankOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleFilled, ControlOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SideBar from "../../../components/SideBar";
import NavBar from "../../../components/NavBar";
import Footer from "../../../components/Footer";
import axiosInstance from "../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../variables/constants";

const { Title, Text } = Typography;
const { confirm } = Modal;
const PRIMARY = "#242166";

/**
 * Gestion des conseils juridiques (personnes morales liées aux dossiers sinistres).
 * CRUD sur /api/conseil-juridique. UI moderne AntD v5.
 */
const ConseilsJuridiques = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");
    const [juridictions, setJuridictions] = useState([]);
    const [form] = Form.useForm();

    const fetchData = () => {
        setLoading(true);
        axiosInstance.get("/api/conseil-juridique")
            .then((res) => setData(Array.isArray(res.data) ? res.data : (res.data?.content ?? [])))
            .catch((e) => { console.error(e); toast.error("Erreur lors du chargement des conseils juridiques."); })
            .finally(() => setLoading(false));
    };
    useEffect(() => { fetchData(); }, []);

    // Juridictions paramétrées (mêmes valeurs que le marquage contentieux) pour les exclusions du contrat.
    useEffect(() => {
        axiosInstance.get("/api/parametre/type/JURIDICTION")
            .then((res) => setJuridictions((res.data || []).map((p) => p?.valeur).filter(Boolean)))
            .catch(() => setJuridictions([]));
    }, []);

    // Le backend stocke les juridictions exclues en texte « a;b;c » ↔ le formulaire manipule un tableau.
    const excluesToArray = (s) => (s ? s.split(";").map((x) => x.trim()).filter(Boolean) : []);
    const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
    const openEdit = (record) => {
        setEditing(record);
        form.setFieldsValue({
            ...record,
            juridictionsExcluesDeplacement: excluesToArray(record.juridictionsExcluesDeplacement),
        });
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        const values = await form.validateFields();
        setSaving(true);
        const isEdit = !!editing;
        const payload = {
            ...(isEdit ? editing : {}),
            ...values,
            juridictionsExcluesDeplacement: (values.juridictionsExcluesDeplacement || []).join(";") || null,
        };
        const req = isEdit
            ? axiosInstance.put("/api/conseil-juridique", payload)
            : axiosInstance.post("/api/conseil-juridique", payload);
        req
            .then(() => {
                toast.success(isEdit ? "Conseil juridique modifié." : "Conseil juridique ajouté.");
                setModalOpen(false);
                fetchData();
            })
            .catch((e) => { console.error(e); toast.error("Erreur lors de l'enregistrement."); })
            .finally(() => setSaving(false));
    };

    const handleDelete = (record) => {
        confirm({
            title: "Supprimer ce conseil juridique ?",
            icon: <ExclamationCircleFilled />,
            content: record.nom,
            okText: "Supprimer", okButtonProps: { danger: true }, cancelText: "Annuler",
            onOk: () => {
                const userId = LOGGED_USER?.codeId;
                const url = userId
                    ? `/api/conseil-juridique/${record.conseilJuridiqueId}/${userId}`
                    : `/api/conseil-juridique/${record.conseilJuridiqueId}`;
                return axiosInstance.delete(url)
                    .then(() => { toast.success("Conseil juridique supprimé."); fetchData(); })
                    .catch((e) => { console.error(e); toast.error("Erreur lors de la suppression."); });
            },
        });
    };

    const filtered = data.filter((c) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return [c.nom, c.adresse, c.telephone1, c.telephone2, c.email]
            .filter(Boolean).some((v) => v.toLowerCase().includes(q));
    });

    const columns = [
        { title: "Nom", dataIndex: "nom", key: "nom", render: (t) => <Text strong>{t}</Text> },
        { title: "Adresse", dataIndex: "adresse", key: "adresse", render: (t) => t || "—" },
        {
            title: "Téléphone(s)", key: "tel",
            render: (_, c) => [c.telephone1, c.telephone2].filter(Boolean).join(" / ") || "—",
        },
        { title: "Email", dataIndex: "email", key: "email", render: (t) => t || "—" },
        {
            title: "Conditions contractuelles", key: "contrat",
            render: (_, c) => {
                const exclues = excluesToArray(c.juridictionsExcluesDeplacement);
                if (c.plafondDeplacementsParDossier == null && !exclues.length) return <Text type="secondary">—</Text>;
                return (
                    <Space direction="vertical" size={2}>
                        {c.plafondDeplacementsParDossier != null && (
                            <Tag color="blue" style={{ margin: 0 }}>Déplacements : {c.plafondDeplacementsParDossier} max / dossier</Tag>
                        )}
                        {exclues.length > 0 && (
                            <Tooltip title={exclues.join(", ")}>
                                <Tag color="red" style={{ margin: 0 }}>{exclues.length} juridiction(s) exclue(s)</Tag>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: "", key: "action", align: "center", width: 110,
            render: (_, c) => (
                <Space>
                    <Tooltip title="Modifier"><Button type="text" icon={<EditOutlined />} onClick={() => openEdit(c)} /></Tooltip>
                    <Tooltip title="Supprimer"><Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(c)} /></Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"PARAMETRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: "#111827" }}>
                                        <BankOutlined style={{ marginRight: 8, color: PRIMARY }} />Conseils juridiques
                                    </Title>
                                    <Text type="secondary">Cabinets / avocats liés aux dossiers sinistres — {data.length} enregistré(s)</Text>
                                </div>
                                <Space>
                                    <Button icon={<ControlOutlined />} onClick={() => navigate("/plafonds-conseils-juridiques")}>
                                        Plafonds par dossier
                                    </Button>
                                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ background: PRIMARY, border: "none", borderRadius: 8, fontWeight: 600 }}>
                                        Nouveau conseil juridique
                                    </Button>
                                </Space>
                            </div>

                            <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                <Input.Search
                                    allowClear placeholder="Rechercher (nom, adresse, téléphone, email)…"
                                    value={search} onChange={(e) => setSearch(e.target.value)}
                                    style={{ maxWidth: 360, marginBottom: 14 }}
                                />
                                <Table
                                    rowKey="conseilJuridiqueId" loading={loading} columns={columns} dataSource={filtered}
                                    scroll={{ x: "max-content" }}
                                    locale={{ emptyText: <Empty description="Aucun conseil juridique" /> }}
                                    pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} conseil(s) juridique(s)` }}
                                />
                            </Card>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>

            <Modal
                open={modalOpen}
                title={editing ? "Modifier le conseil juridique" : "Nouveau conseil juridique"}
                onCancel={() => setModalOpen(false)}
                onOk={handleSubmit}
                confirmLoading={saving}
                okText={editing ? "Enregistrer" : "Ajouter"}
                cancelText="Annuler"
                okButtonProps={{ style: { background: PRIMARY, border: "none" } }}
                destroyOnClose
            >
                <Form form={form} layout="vertical" requiredMark={false} style={{ marginTop: 8 }}>
                    <Form.Item name="nom" label="Nom / Raison sociale" rules={[{ required: true, message: "Le nom est obligatoire" }]}>
                        <Input placeholder="Ex : Cabinet d'avocats ..." />
                    </Form.Item>
                    <Form.Item name="adresse" label="Adresse">
                        <Input placeholder="Adresse" />
                    </Form.Item>
                    <Space style={{ display: "flex" }} size="middle">
                        <Form.Item name="telephone1" label="Téléphone 1" style={{ flex: 1 }}>
                            <Input placeholder="Téléphone principal" />
                        </Form.Item>
                        <Form.Item name="telephone2" label="Téléphone 2" style={{ flex: 1 }}>
                            <Input placeholder="Téléphone secondaire" />
                        </Form.Item>
                    </Space>
                    <Form.Item name="email" label="Email" rules={[{ type: "email", message: "Email invalide" }]}>
                        <Input placeholder="email@cabinet.com" />
                    </Form.Item>

                    <Divider style={{ margin: "8px 0 16px" }}>Conditions contractuelles</Divider>
                    <Form.Item
                        name="plafondDeplacementsParDossier"
                        label="Nombre max de paiements de frais de déplacement par dossier"
                        extra="1 paiement = 1 audience. Vide = illimité. Ex : 15 → la 16e ligne de déplacement d'un dossier est rejetée."
                    >
                        <InputNumber min={0} style={{ width: "100%" }} placeholder="Illimité" />
                    </Form.Item>
                    <Form.Item
                        name="juridictionsExcluesDeplacement"
                        label="Juridictions non prises en charge (frais de déplacement)"
                        extra="Les déplacements des dossiers jugés dans ces juridictions sont refusés. Si une exclusion existe et que le dossier n'a pas de juridiction renseignée, le déplacement est bloqué."
                    >
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Ex : Tribunal de Lomé…"
                            options={juridictions.map((j) => ({ value: j, label: j }))}
                        />
                    </Form.Item>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Rappel : les frais de PV de constat et de gestion sont payables <b>une seule fois par dossier</b> (règle appliquée automatiquement).
                    </Text>
                </Form>
            </Modal>
        </div>
    );
};

export default ConseilsJuridiques;
